import React, { useEffect, useState, useMemo } from "react";
import { fetchWithToken } from "../../../api/apiutils";
import { useSearchParams } from "react-router-dom";
import { ErrorOutline, Close as CloseIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
} from "@mui/material";
import Paging from "./ActiveUserPaging";
import ApplicationsDialog from "./ApplicationsDialog";

const UserDialogModal = ({
  open,
  onClose,
  userId,
  type,
  onViewResultClick,
  onGetLinksClick,
  actionDateFrom,
}) => {
  const [dialogData, setDialogData] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [dialogTotalRecords, setDialogTotalRecords] = useState(0);
  const [currentDialogPage, setCurrentDialogPage] = useState(1);
  const [params, setParams] = useState(null);

  const [appsOpen, setAppsOpen] = useState(false);
  const [selectedApps, setSelectedApps] = useState([]);

  const dialogRowsPerPage = 10;
  const totalDialogPages = useMemo(
    () => Math.ceil(dialogTotalRecords / dialogRowsPerPage),
    [dialogTotalRecords],
  );

  const fetchDialogData = async (page) => {
    if (!userId || !type) return;

    setDialogLoading(true);
    let endpoint = "";

    switch (type) {
      case "likes": {
        const params = new URLSearchParams({
          UserId: userId,
          page,
          pageLimit: dialogRowsPerPage,
        });
        if (actionDateFrom) {
          params.append("DateFrom", actionDateFrom);
        }
        endpoint = `/api/lots/favourite/byUser?${params.toString()}`;
        setParams(params);
        break;
      }

      case "selection": {
        const params = new URLSearchParams({
          UserId: userId,
          page,
          pageLimit: dialogRowsPerPage,
        });
        if (actionDateFrom) {
          params.append("DateFrom", actionDateFrom);
        }
        endpoint = `/api/lots/selection/byUser?${params.toString()}`;
        break;
      }

      case "analysis": {
        const params = new URLSearchParams({
          UserId: userId,
          page,
          pageLimit: dialogRowsPerPage,
        });
        if (actionDateFrom) {
          params.append("DateFrom", actionDateFrom);
        }
        endpoint = `/api/lots/analyzed/byUser?${params.toString()}`;
        break;
      }

      case "paidfilter": {
        const params = new URLSearchParams({
          UserId: userId,
          page,
          pageLimit: dialogRowsPerPage,
          HasPaidFilters: "true",
        });
        if (actionDateFrom) {
          params.append("DateOfCreationFrom", actionDateFrom);
        }
        endpoint = `/api/filters/byUser?${params.toString()}`;
        break;
      }

      case "textFilters": {
        const params = new URLSearchParams({
          UserId: userId,
          page,
          pageLimit: dialogRowsPerPage,
        });
        if (actionDateFrom) {
          params.append("DateOfCreationFrom", actionDateFrom);
        }
        endpoint = `/api/filters/byUser?${params.toString()}`;
        break;
      }

      default:
        return;
    }

    try {
      const response = await fetchWithToken(endpoint);
      const responseData = await response.json();
      setDialogData(responseData.data || []);
      setDialogTotalRecords(responseData.all || 0);
    } catch (error) {
      setDialogData([]);
      console.error("Ошибка загрузки данных:", error);
    } finally {
      setDialogLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setCurrentDialogPage(1);
      fetchDialogData(1);
    }
  }, [open, userId, type]);

  const handlePageChange = (page) => {
    setCurrentDialogPage(page);
    fetchDialogData(page);
  };

  const handleApplicantsClick = (item) => {
    setSelectedApps(item?.result?.applications ?? []);
    setAppsOpen(true);
  };

  const renderLogCell = (log) => {
    if (!log) return "Нет данных";
    if (log.isException) return <ErrorOutline color="error" />;

    const start = new Date(log.dateOfStart);
    const end = new Date(log.dateOfEnd);
    const duration = end - start;

    if (isNaN(duration)) return "Нет данных";
    const seconds = Math.floor(duration / 1000);
    const milliseconds = duration % 1000;

    if (seconds === 0) return `Время: ${(duration / 1000).toFixed(2)} сек`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return (
      <div>
        Время: {minutes > 0 ? `${minutes} мин ` : ""}
        {remainingSeconds > 0 ? `${remainingSeconds} сек` : ""}
      </div>
    );
  };

  const transformLogs = (logs) => {
    return logs.reduce((acc, log) => {
      acc[log.code] = log;
      return acc;
    }, {});
  };

  const renderLogCombination = (logsMap) => {
    return (
      <>
        {logsMap["ProductSelection"] && logsMap["Search"] && (
          <>
            <div>
              Подбор товара {renderLogCell(logsMap["ProductSelection"])}
            </div>
            <div>Поиск {renderLogCell(logsMap["Search"])}</div>
          </>
        )}
        {logsMap["SendTextRu"] && logsMap["TetxRuResult"] && (
          <>
            <div>
              Отправлен в Текст Ру {renderLogCell(logsMap["SendTextRu"])}
            </div>
            <div>
              Результат Текст Ру {renderLogCell(logsMap["TetxRuResult"])}
            </div>
          </>
        )}
      </>
    );
  };

  const headers =
    type === "paidfilter"
      ? ["#", "Фильтр", "Имя", "Дата создания", "Период действия"]
      : type === "likes"
        ? [
            "#",
            "Дата лайка",
            "Номер",
            "Имя",
            "Статус",
            "Описание",
            "Поставщик участвовал",
            "Заявки на конкретный лот",
            "Торги",
            "Сумма",
            "Цена за единицу",
            "Единица измерения",
            "Дата начала",
            "Дата окончания",
          ]
        : type === "textFilters"
          ? ["#", "Дата", "Регион", "Текст", "Подписка", "Сумма"]
          : type === "analysis"
            ? [
                "#",
                "Наименование (Лот)",
                "Дата Анализа",
                "Номер лота",
                "Тип лота",
                "Техническая спецификация",
                "Анализ ТС",
                "Подбор товара",
                "Запланированные лоты",
                "Анализ конкурентов",
                "Результаты аналогичных лотов",
              ]
            : [
                "#",
                "Номер",
                "Имя",
                "Статус",
                "Описание",
                "Торги",
                "Сумма",
                "Цена за единицу",
                "Единица измерения",
                "Дата начала",
                "Дата окончания",
              ];

  const startNumber = (currentDialogPage - 1) * dialogRowsPerPage + 1;

  const exportData = async () => {
    console.log(params.toString());

    try {
      const res = await fetchWithToken(
        `/api/lots/favourite/export?${params.toString()}`,
      );

      if (!res.ok) {
        throw new Error("Ошибка при экспорте");
      }

      // Получаем бинарные данные (Excel)
      const blob = await res.blob();

      // Создаём временную ссылку
      const url = window.URL.createObjectURL(blob);

      // Создаём <a> для скачивания
      const link = document.createElement("a");
      link.href = url;

      // Можно имя задать жёстко или попробовать достать из заголовка
      const contentDisposition = res.headers.get("Content-Disposition");
      let fileName = "export.xlsx";

      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) fileName = match[1];
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);

      // Кликаем по ссылке → скачивание
      link.click();

      // Убираем за собой
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Ошибка при экспорте:", error);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            width: "90vw",
            height: "80vh",
            maxWidth: "none",
            maxHeight: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            // flexDirection: "row",
            justifyContent: "space-between",
            marginBottom: 3,
            marginTop: 3,
          }}
        >
          Подробности
          <IconButton
            onClick={onClose}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <Button
            sx={{ marginRight: 5 }}
            variant="contained"
            color="primary"
            onClick={() => {
              exportData();
            }}
          >
            Выгрузить
          </Button>
        </DialogTitle>

        {/* <Box
          
        >
          <Button variant="contained" color="primary">
            sdasd
          </Button>
        </Box> */}

        <DialogContent>
          {dialogLoading ? (
            <CircularProgress />
          ) : !dialogData.length ? (
            <Typography>Нет данных</Typography>
          ) : (
            <>
              <TableContainer
                component={Paper}
                sx={{ minHeight: 600, borderRadius: 4, boxShadow: 3 }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {headers.map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            fontWeight: "bold",
                            backgroundColor: "#f5f5f5",
                            textAlign: "center",
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dialogData.map((item, index) => {
                      const logsMap = transformLogs(item.logs || []);
                      return (
                        <TableRow
                          key={item.id || index}
                          sx={{
                            "&:nth-of-type(even)": {
                              backgroundColor: "#f9f9f9",
                            },
                            "&:hover": { backgroundColor: "#e0f7fa" },
                          }}
                        >
                          <TableCell sx={{ textAlign: "center" }}>
                            {startNumber + index}
                          </TableCell>
                          {type === "paidfilter" && (
                            <>
                              <TableCell>
                                {item.paidFilters?.join(", ") || "N/A"}
                              </TableCell>
                              <TableCell>{item.text || "N/A"}</TableCell>
                              <TableCell>
                                {item.dateOfCreation
                                  ? new Date(
                                      item.dateOfCreation,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.validityPeriod
                                  ? new Date(
                                      item.validityPeriod,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </>
                          )}
                          {type === "likes" && (
                            <>
                              <TableCell>
                                {item.likedAt
                                  ? new Date(item.likedAt).toLocaleString(
                                      "ru-RU",
                                      {
                                        dateStyle: "short",
                                        timeStyle: "short",
                                        timeZone: "Asia/Almaty",
                                      },
                                    )
                                  : "N/A"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                {item.number || "N/A"}
                              </TableCell>
                              <TableCell>{item.name || "N/A"}</TableCell>
                              <TableCell>{item.status || "N/A"}</TableCell>
                              <TableCell>{item.description || "N/A"}</TableCell>
                              <TableCell>
                                {item.result?.userParticipated === true
                                  ? "Да"
                                  : item.result?.userParticipated === false
                                    ? "Нет"
                                    : "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.result?.totalApplicants > 0 ? (
                                  <Button
                                    variant="text"
                                    onClick={() => handleApplicantsClick(item)}
                                  >
                                    {item.result.totalApplicants}
                                  </Button>
                                ) : (
                                  "N/A"
                                )}
                              </TableCell>
                              <TableCell>{item.trade || "N/A"}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                {item.amount || "N/A"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                {item.unitPrice || "N/A"}
                              </TableCell>
                              <TableCell>{item.measure || "N/A"}</TableCell>
                              <TableCell>
                                {item.dateOfStart
                                  ? new Date(
                                      item.dateOfStart,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.dateOfEnd
                                  ? new Date(
                                      item.dateOfEnd,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </>
                          )}
                          {type === "selection" && (
                            <>
                              <TableCell>{item.number || "N/A"}</TableCell>
                              <TableCell>{item.name || "N/A"}</TableCell>
                              <TableCell>{item.status || "N/A"}</TableCell>
                              <TableCell>{item.description || "N/A"}</TableCell>
                              <TableCell>{item.trade || "N/A"}</TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                {item.amount?.toLocaleString() || "N/A"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                {item.unitPrice?.toLocaleString() || "N/A"}
                              </TableCell>
                              <TableCell>{item.measure || "N/A"}</TableCell>
                              <TableCell>
                                {item.dateOfStart
                                  ? new Date(
                                      item.dateOfStart,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.dateOfEnd
                                  ? new Date(
                                      item.dateOfEnd,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </TableCell>
                            </>
                          )}
                          {type === "textFilters" && (
                            <>
                              <TableCell>
                                {item.dateOfCreation
                                  ? new Date(
                                      item.dateOfCreation,
                                    ).toLocaleString()
                                  : "N/A"}
                              </TableCell>
                              <TableCell>{item.regionCode}</TableCell>
                              <TableCell>{item.text}</TableCell>
                              <TableCell>
                                {item.subscribe ? "Да" : "Нет"}
                              </TableCell>
                              <TableCell>
                                {item.amountFrom && item.amountTo
                                  ? `от ${item.amountFrom} - до ${item.amountTo}`
                                  : item.amountFrom
                                    ? `от ${item.amountFrom}`
                                    : item.amountTo
                                      ? `до ${item.amountTo}`
                                      : "Нет"}
                              </TableCell>
                            </>
                          )}
                          {type === "analysis" && (
                            <>
                              <TableCell>{item.name || "N/A"}</TableCell>
                              <TableCell>
                                {item.dateOfCreation
                                  ? new Date(
                                      item.dateOfCreation,
                                    ).toLocaleString("ru-RU", {
                                      dateStyle: "short",
                                      timeStyle: "short",
                                    })
                                  : "N/A"}
                              </TableCell>
                              <TableCell>{item.number || "N/A"}</TableCell>
                              <TableCell>{item.subject || "N/A"}</TableCell>
                              <TableCell>
                                {item.techSpecificationState || "N/A"}
                              </TableCell>
                              <TableCell>
                                {item.hasSummary && (
                                  <>
                                    {renderLogCell(logsMap["Summary"])}
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      sx={{ mt: 1 }}
                                      onClick={() => onViewResultClick(item.id)}
                                    >
                                      Посмотреть результат
                                    </Button>
                                  </>
                                )}
                              </TableCell>
                              <TableCell>
                                {item.webAnalysisState && (
                                  <>
                                    {renderLogCombination(logsMap)}
                                    <Button
                                      variant="outlined"
                                      color="primary"
                                      size="small"
                                      sx={{ mt: 1 }}
                                      onClick={() => onGetLinksClick(item.id)}
                                    >
                                      Получить ссылки
                                    </Button>
                                  </>
                                )}
                              </TableCell>
                              <TableCell>
                                {item.plannedLots}{" "}
                                {renderLogCell(logsMap["PlannedLots"])}
                              </TableCell>
                              <TableCell>
                                {item.competitors}{" "}
                                {renderLogCell(logsMap["Competitors"])}
                              </TableCell>
                              <TableCell>
                                {item.similarLots}{" "}
                                {renderLogCell(logsMap["SimilarLots"])}
                              </TableCell>
                            </>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Paging
                localPage={currentDialogPage}
                totalPages={totalDialogPages}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="primary">
            Закрыть
          </Button>
        </DialogActions>
        <ApplicationsDialog
          open={appsOpen}
          onClose={() => setAppsOpen(false)}
          applications={selectedApps}
        />
      </Dialog>
    </>
  );
};

export default UserDialogModal;
